/**
 * GraphQL queries and hooks — the four Technology Layer widgets added by
 * issue #1654 (part of epic #1650): Technology Footprint, Tech
 * Rationalization, Platform Usage, and Tech Adoption Timeline.
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';

/**
 * Query for the Technology Footprint widget (issue #1654). `category`
 * filters by the technology's resolved product-family name; `limit`
 * matches the spec's documented `?limit=10` example (default 10, capped
 * at 100 server-side).
 */
export const GET_TECHNOLOGY_FOOTPRINT = gql`
  query GetTechnologyFootprint($category: String, $limit: Int) {
    technologyFootprint(category: $category, limit: $limit) {
      technologies {
        id
        name
        category
        serviceCount
        strategicStatus
        trend
      }
    }
  }
`;

export interface TechnologyFootprintEntry {
  id: string;
  name: string;
  category: string;
  serviceCount: number;
  strategicStatus: string;
  trend: string | null;
}

export interface TechnologyFootprintData {
  technologyFootprint: {
    technologies: TechnologyFootprintEntry[];
  };
}

export interface TechnologyFootprintVariables {
  category?: string;
  limit?: number;
}

/** Hook for the Technology Footprint widget's service-count-ranked technology list. */
export function useTechnologyFootprint(
  category?: string,
  limit?: number
): QueryResult<TechnologyFootprintData, TechnologyFootprintVariables> {
  return useQuery(GET_TECHNOLOGY_FOOTPRINT, {
    variables: { category, limit },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Tech Rationalization widget (issue #1654). Administrator
 * only — no query params are documented for this endpoint.
 */
export const GET_TECH_RATIONALIZATION = gql`
  query GetTechRationalization {
    techRationalization {
      opportunities {
        category
        issue
        recommendation
        technologies
        technology
        strategicStatus
        serviceCount
        potentialSavings
      }
    }
  }
`;

export interface RationalizationOpportunity {
  category: string;
  issue: string;
  recommendation: string;
  technologies?: string[] | null;
  technology?: string | null;
  strategicStatus?: string | null;
  serviceCount?: number | null;
  potentialSavings?: string | null;
}

export interface TechRationalizationData {
  techRationalization: {
    opportunities: RationalizationOpportunity[];
  };
}

/** Hook for the Tech Rationalization widget's consolidation opportunities. */
export function useTechRationalization(): QueryResult<TechRationalizationData> {
  return useQuery(GET_TECH_RATIONALIZATION, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Platform Usage widget (issue #1654). Reuses the existing
 * `UsageRollupRepository` rollup (epic #1448) rather than a new
 * aggregation — see `backend/src/graphql/resolvers/platformUsage.ts` and
 * ADR-1654. Administrator or `analytics:read` role required.
 */
export const GET_PLATFORM_USAGE = gql`
  query GetPlatformUsage {
    platformUsage {
      periodStart
      periodEnd
      activeUsers {
        timestamp
        value
      }
      topRoutes {
        name
        count
      }
      topFeatures {
        name
        count
      }
    }
  }
`;

export interface PlatformUsageKeyCount {
  name: string;
  count: number;
}

export interface PlatformUsageTimeSeriesPoint {
  timestamp: string;
  value: number;
}

export interface PlatformUsageData {
  platformUsage: {
    periodStart: string;
    periodEnd: string;
    activeUsers: PlatformUsageTimeSeriesPoint[];
    topRoutes: PlatformUsageKeyCount[];
    topFeatures: PlatformUsageKeyCount[];
  };
}

/** Hook for the Platform Usage widget's trailing-30-day telemetry summary. */
export function usePlatformUsage(): QueryResult<PlatformUsageData> {
  return useQuery(GET_PLATFORM_USAGE, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Tech Adoption Timeline widget (issue #1654). `category`
 * filters by resolved product-family name; `months` matches the spec's
 * documented `?months=12` example (default 12, capped at 60 server-side).
 */
export const GET_TECH_ADOPTION_TIMELINE = gql`
  query GetTechAdoptionTimeline($category: String, $months: Int) {
    techAdoptionTimeline(category: $category, months: $months) {
      timeline {
        month
        technologies {
          name
          count
        }
      }
    }
  }
`;

export interface TechAdoptionTimelineEntry {
  name: string;
  count: number;
}

export interface TechAdoptionTimelineMonth {
  month: string;
  technologies: TechAdoptionTimelineEntry[];
}

export interface TechAdoptionTimelineData {
  techAdoptionTimeline: {
    timeline: TechAdoptionTimelineMonth[];
  };
}

export interface TechAdoptionTimelineVariables {
  category?: string;
  months?: number;
}

/** Hook for the Tech Adoption Timeline widget's monthly cumulative adoption counts. */
export function useTechAdoptionTimeline(
  category?: string,
  months?: number
): QueryResult<TechAdoptionTimelineData, TechAdoptionTimelineVariables> {
  return useQuery(GET_TECH_ADOPTION_TIMELINE, {
    variables: { category, months },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
